import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatMoney } from '@belong/common';
import { Checkbox, Divider, Text, Toast } from '@belong/ui';
import Button from 'components/Button/Button';
import { BUTTON_TYPES } from 'components/Button/buttonTypes';
import {
  MaintenanceQuoteConsentStatus,
  MaintenanceQuoteConsumerModel,
  MaintenanceQuoteRejectionReason,
  QuoteTypeOfWorkLabels,
} from 'maintenance/types/quote-centric';
import { updateQuoteCentricConsent } from 'store/redux/maintenance/actions';
import Image from 'components/Image/Image';
import clsx from 'clsx';
import { QUOTE_APPROVAL_STATUS_STYLES } from 'maintenance/constants';
import { isQuoteApproved, isQuoteRejected } from 'maintenance/utils';
import { CostItemsGroup } from './CostItemsGroup';

type Decision = 'approved' | 'rejected';

const REJECTION_REASONS: {
  title: string;
  value: MaintenanceQuoteRejectionReason;
}[] = [
  { title: 'Questions About Pricing', value: 'TooExpensive' },
  {
    title: 'Questions About The Work To Be Done',
    value: 'WantSecondOpinionFromAnotherPro',
  },
  { title: 'Other Questions', value: 'Other' },
];

type QuoteDetailsProps = {
  maintenanceUniqueId: string;
  quote: MaintenanceQuoteConsumerModel;
  onQuotesChange: () => Promise<void>;
  hasMultipleQuotes?: boolean;
};

export const QuoteDetails = ({
  maintenanceUniqueId,
  quote,
  onQuotesChange,
  hasMultipleQuotes,
}: QuoteDetailsProps) => {
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [rejectionReason, setRejectionReason] =
    useState<MaintenanceQuoteRejectionReason | null>(null);
  const [notes, setNotes] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastMessage = useRef('');

  const isRejected = isQuoteRejected(quote);
  const isApproved = !isRejected && isQuoteApproved(quote);
  const needsMyApproval = quote.myApproval?.consentStatus === 'Required';
  const tag = isRejected
    ? 'Rejected'
    : isApproved
      ? 'Approved'
      : needsMyApproval
        ? 'Pending Approval'
        : undefined;

  const canDecide =
    quote.status !== 'Rejected' &&
    quote.status !== 'NotSelected' &&
    !quote.approvals.some((a) => a.consentStatus === 'Rejected') &&
    quote.myApproval?.consentStatus === 'Required';

  const handleDecisionChange = (value: Decision) => {
    setDecision(value);
    if (value === 'approved') {
      setRejectionReason(null);
      setNotes('');
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!decision) return;
    if (decision === 'rejected' && !rejectionReason) return;

    try {
      setIsUpdating(true);
      if (decision === 'approved') {
        await dispatch(
          updateQuoteCentricConsent(maintenanceUniqueId, quote.uniqueId, {
            isApproved: true,
          }),
        );
        toastMessage.current = 'Approved';
      } else {
        await dispatch(
          updateQuoteCentricConsent(maintenanceUniqueId, quote.uniqueId, {
            isApproved: false,
            rejectionReason,
            notes:
              rejectionReason === 'Other' && notes.trim()
                ? notes.trim()
                : undefined,
          }),
        );
        toastMessage.current = 'Response submitted';
      }
      setToastVisible(true);
      await onQuotesChange();
    } catch (e) {
      console.error(e);
      toastMessage.current = 'Something went wrong. Please try again.';
      setToastVisible(true);
    } finally {
      setIsUpdating(false);
    }
  }, [
    decision,
    dispatch,
    maintenanceUniqueId,
    notes,
    onQuotesChange,
    quote.uniqueId,
    rejectionReason,
  ]);

  const canSubmit =
    decision === 'approved' ||
    (decision === 'rejected' &&
      !!rejectionReason &&
      (rejectionReason !== 'Other' || notes.trim().length >= 10));

  const myParty = quote.myApproval?.party ?? null;
  const myPercentage = quote.myApproval?.percentage ?? 0;
  const myAmount =
    quote.myApproval?.estimatedAmount ?? quote.myEstimatedAmount ?? 0;

  // A discount is stored as a negative-value `Discount` cost item. The total is
  // already net of it; surface it as its own line so the savings are visible.
  const discountItem = quote.costItems.find(
    (item) => item.type === 'Discount' && item.cost < 0,
  );
  const discountAmount = discountItem ? Math.abs(discountItem.cost) : 0;

  const otherApproval = quote.approvals.find(
    (a) => a.party !== myParty && a.party !== 'Belong' && a.percentage > 0,
  );
  const otherPercentage = otherApproval?.percentage ?? 0;
  const otherAmount = otherApproval
    ? quote.totalAmount * (otherApproval.percentage / 100)
    : 0;
  const otherPartyLabel = otherApproval?.party;
  const myConsentStatus = quote.myApproval?.consentStatus;
  const otherDifferentApprovals = quote.approvals.filter(
    (
      a,
    ): a is typeof a & {
      consentStatus: Exclude<MaintenanceQuoteConsentStatus, 'NotRequired'>;
    } =>
      a.party !== myParty &&
      a.party !== 'Belong' &&
      a.consentStatus !== 'NotRequired' &&
      a.consentStatus !== myConsentStatus,
  );
  const displayApprovals =
    otherDifferentApprovals.length > 0 &&
    myParty &&
    myConsentStatus &&
    myConsentStatus !== 'NotRequired'
      ? [
          { party: myParty, consentStatus: myConsentStatus },
          ...otherDifferentApprovals,
        ]
      : otherDifferentApprovals;

  return (
    <div>
      <div
        className={
          hasMultipleQuotes
            ? 'p-sm'
            : 'rounded-t border border-solid border-gray bg-light-gray px-md pt-md pb-xl'
        }
      >
        {!hasMultipleQuotes && tag && (
          <span
            className={clsx(
              'mb-xs inline-block whitespace-nowrap rounded-small px-xs text-p1 text-white',
              isApproved ? 'bg-green' : isRejected ? 'bg-dark-gray' : 'bg-red',
            )}
          >
            {tag}
          </span>
        )}

        <div className="flex items-start justify-between gap-sm">
          <Text fontWeight="semibold">
            {quote.title}
            {quote.typeOfWork
              ? ` • ${QuoteTypeOfWorkLabels[quote.typeOfWork]}`
              : ''}
          </Text>
        </div>

        {displayApprovals.length > 0 && (
          <div className="mt-xs flex flex-wrap gap-x-sm gap-y-2xs border-0 border-t border-dashed border-gray py-xs">
            {displayApprovals.map((a) => {
              const style = QUOTE_APPROVAL_STATUS_STYLES[a.consentStatus];
              const { Icon } = style;
              return (
                <div
                  key={a.party}
                  className={clsx('flex items-center gap-2xs', style.className)}
                >
                  <Icon className="shrink-0" width={12} height={12} />
                  <Text fontWeight="semibold" variant="p1">
                    {style.label(a.party !== myParty ? a.party : undefined)}
                  </Text>
                </div>
              );
            })}
          </div>
        )}

        {quote.scopeOfWork && (
          <Text className="mt-sm">{quote.scopeOfWork}</Text>
        )}

        {quote.assignedPro?.name && (
          <div className="my-xl flex items-center justify-between">
            <Text className="font-semibold">Assigned Pro</Text>
            <div className="flex items-center gap-xs">
              <Text className="font-semibold">{quote.assignedPro.name}</Text>
              <div className="h-xl w-xl overflow-hidden rounded-full">
                <Image src="/common/icon_partner.svg" alt="Belong Pro" />
              </div>
            </div>
          </div>
        )}

        {quote.costItems.length > 0 && (
          <div className="mt-sm">
            <CostItemsGroup costItems={quote.costItems} />
          </div>
        )}

        <Divider borderStyle="dashed" className="my-sm" />

        <div className="flex justify-between">
          <Text fontWeight="semibold">Subtotal</Text>
          <Text fontWeight="semibold" className="text-navy">
            {formatMoney(quote.totalAmount + discountAmount)}
          </Text>
        </div>

        {otherPercentage > 0 && otherPartyLabel && (
          <div className="mt-xs flex justify-between">
            <Text>
              {otherPartyLabel} Responsibility ({otherPercentage}%)
            </Text>
            <Text>{formatMoney(otherAmount)}</Text>
          </div>
        )}

        {discountAmount > 0 && (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold" className="text-green">
              In-Network Discount
            </Text>
            <Text fontWeight="semibold" className="text-green">
              {`-${formatMoney(discountAmount)}`}
            </Text>
          </div>
        )}

        {myPercentage > 0 ? (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold">Your Portion ({myPercentage}%)</Text>
            <div className="flex items-end gap-xs">
              {discountAmount > 0 && (
                <Text variant="p1" className="text-dark-gray line-through">
                  {formatMoney(
                    (quote.totalAmount + discountAmount) * (myPercentage / 100),
                  )}
                </Text>
              )}
              <Text fontWeight="semibold" className="text-navy">
                {formatMoney(myAmount)}
              </Text>
            </div>
          </div>
        ) : (
          <div className="mt-xs flex justify-between">
            <Text fontWeight="semibold">Your Portion (0%)</Text>
            <Text fontWeight="semibold" className="text-navy">
              Free
            </Text>
          </div>
        )}
      </div>
      {canDecide && (
        <div
          className={clsx(
            'mt-lg flex flex-col bg-darker-navy p-sm',
            hasMultipleQuotes ? 'rounded-b' : 'rounded',
          )}
        >
          <Text fontWeight="semibold" className="mb-sm text-white">
            Let&apos;s get your home taken care of!
          </Text>

          <div className="flex flex-col gap-sm">
            <Checkbox
              id={`${quote.uniqueId}-approved`}
              checked={decision === 'approved'}
              variant="tertiary"
              label="YES, PROCEED WITH THE WORK."
              onChange={() => handleDecisionChange('approved')}
            />
            <Checkbox
              id={`${quote.uniqueId}-rejected`}
              checked={decision === 'rejected'}
              variant="tertiary"
              label="I NEED MORE INFORMATION"
              onChange={() => handleDecisionChange('rejected')}
            />
          </div>

          {decision === 'rejected' && (
            <div className="mt-lg flex flex-col gap-sm">
              <Text fontWeight="semibold" className="text-white">
                We&apos;re committed to solving this as quickly as possible. How
                can we help?
              </Text>
              {REJECTION_REASONS.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`${quote.uniqueId}-reason-${option.value}`}
                  checked={rejectionReason === option.value}
                  variant="tertiary"
                  label={option.title}
                  onChange={() => setRejectionReason(option.value)}
                />
              ))}
              {rejectionReason === 'Other' && (
                <textarea
                  className="mt-sm w-full rounded bg-white/10 p-sm text-white placeholder:text-white/50 focus:outline-none"
                  placeholder="Please explain (min 10 characters)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={200}
                  rows={3}
                />
              )}
            </div>
          )}

          <div className="mt-lg flex w-full justify-end">
            <Button
              label="SUBMIT"
              buttonType={BUTTON_TYPES.DEFAULT}
              onClick={handleSubmit}
              disabled={isUpdating || !canSubmit || !decision}
            />
          </div>
        </div>
      )}

      <Toast
        autocloseDuration={5000}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      >
        {toastMessage.current}
      </Toast>
    </div>
  );
};
